import { Button, Icon } from '@chakra-ui/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from '../../../assets/icons'
import FilterModal from '../../modals/filterModal'

interface FilterButtonProps {
  label: string
  options: { id: string; name: string }[]
  onApply: (selected: string[]) => void
  /** Pre-select these options on mount (and keep highlighted). */
  defaultSelected?: string[]
  singleSelect?: boolean
  page?: number
  maxPage?: number
  onPageChange?: (page: number) => void
  search?: string
  setSearch?: (search: string) => void
  /** Infinite scroll: load more when user scrolls to bottom */
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  /** Show spinner when the filter options query is loading (e.g. while searching) */
  isLoading?: boolean
  /** Flows exposes the same filter controls on phones as on desktop. */
  showOnMobile?: boolean
}

const FilterButton: React.FC<FilterButtonProps> = ({
  label,
  options,
  onApply,
  defaultSelected,
  singleSelect = false,
  page,
  maxPage,
  onPageChange,
  search,
  setSearch,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isLoading,
  showOnMobile = false,
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState<string[]>(defaultSelected ?? [])
  const isHighlighted = selectedOptions.length > 0

  const handleApply = (selected: string[]) => {
    setSelectedOptions(selected)
    setIsOpen(false)
    onApply(selected)
  }

  return (
    <FilterModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      options={options}
      selectedOptions={selectedOptions}
      singleSelect={singleSelect}
      onApply={handleApply}
      title={t('common.labels.filterBy', { label })}
      {...(page !== undefined ? { page, maxPage, onPageChange } : {})}
      {...(search !== undefined ? { search, setSearch } : {})}
      {...(onLoadMore !== undefined ? { onLoadMore, hasMore, isLoadingMore } : {})}
      {...(isLoading !== undefined ? { isLoading } : {})}
    >
      <Button
        display={{ base: showOnMobile ? 'inline-flex' : 'none', md: 'inline-flex' }}
        onClick={() => setIsOpen(true)}
        sx={{
          bg: isHighlighted ? 'purple.400' : 'transparent',
          color: isHighlighted ? 'white' : 'black',
          fontWeight: isHighlighted ? '600' : '400',
        }}
        variant="white"
        size="sm"
        h="32px"
        px="14px"
        fontSize="13px"
        fontWeight="600"
        borderRadius="8px"
      >
        {label}
        <Icon width="12px" height="12px" fill="none" color={isHighlighted ? 'white' : 'solid.500'} ml={1.5}>
          <ChevronDownIcon />
        </Icon>
      </Button>
    </FilterModal>
  )
}

export default FilterButton
